/**
 * CommandPalette — popup Cmd+K com busca fuzzy + navegação por teclado.
 *
 * Renderizar uma vez no _app.tsx (ou Layout):
 *   import { CommandPalette } from "@/components/CommandPalette";
 *   <CommandPalette />
 *
 * Atalhos:
 *   ⌘K / Ctrl+K  — abrir
 *   ↑ ↓          — navegar
 *   ↵            — executar
 *   Esc          — fechar
 *
 * Visual: Fraunces nos títulos das seções (identidade visual da plataforma).
 */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/components/ThemeProvider";
import { useKeyboard } from "@/lib/hooks";
import { COMMANDS, scoreCommand, type Command, type CommandContext } from "@/lib/commands";

type SectionKey = NonNullable<Command["section"]>;

const SECTION_ORDER: SectionKey[] = ["create", "navigate", "settings", "help"];

const SECTION_TITLES: Record<SectionKey, string> = {
  create:   "commands.section.create",
  navigate: "commands.section.navigate",
  settings: "commands.section.settings",
  help:     "commands.section.help",
};


export function CommandPalette() {
  const { t } = useTranslation();
  const router = useRouter();
  const { theme, locale, setTheme, setLocale } = useTheme();
  const [me, setMe] = useState<{ role?: string }>({});
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Pega o role do user via /me (assume que está em localStorage ou similar — fallback "user")
  useEffect(() => {
    try {
      const raw = localStorage.getItem("miller.me.role.v1");
      if (raw) setMe({ role: raw });
    } catch {}
  }, []);

  // Cmd+K abre/fecha
  useKeyboard("k", () => setOpen(o => !o), { meta: true });
  // Esc fecha (independe de meta)
  useKeyboard("Escape", () => setOpen(false), { enabled: open, preventDefault: false });

  // Foco no input ao abrir
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    } else {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  // Contexto pra os perform()
  const ctx: CommandContext = useMemo(() => ({
    navigate: (path: string) => { setOpen(false); router.push(path); },
    role: me.role ?? "user",
    setTheme: (t) => { setTheme(t); setOpen(false); },
    setLocale: (l) => { setLocale(l); setOpen(false); },
  }), [router, me.role, setTheme, setLocale]);

  // Comandos visíveis + filtrados + agrupados
  const groupedCommands = useMemo(() => {
    const visible = COMMANDS.filter(c => !c.visible || c.visible(ctx));
    const scored = visible
      .map(c => ({ cmd: c, score: scoreCommand(c, query, t(c.labelKey)) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score);

    const groups: Record<SectionKey, Command[]> = { create: [], navigate: [], settings: [], help: [] };
    for (const { cmd } of scored) {
      const section = (cmd.section ?? "navigate") as SectionKey;
      groups[section].push(cmd);
    }
    return groups;
  }, [query, ctx, t]);

  // Lista flat pra navegação por teclado
  const flatList = useMemo(() => {
    const list: Command[] = [];
    for (const sec of SECTION_ORDER) list.push(...groupedCommands[sec]);
    return list;
  }, [groupedCommands]);

  // Reset active quando query muda
  useEffect(() => { setActive(0); }, [query]);

  // ↑/↓/Enter na lista
  function handleKey(e: React.KeyboardEvent) {
    if (flatList.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(a => (a + 1) % flatList.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(a => (a - 1 + flatList.length) % flatList.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      flatList[active]?.perform(ctx);
    }
  }

  if (typeof window === "undefined" || !open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[400] flex items-start justify-center pt-[12vh] px-4 animate-modal-fade">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Panel */}
      <div className="relative w-full max-w-xl bg-app-surface rounded-2xl shadow-2xl border border-app overflow-hidden animate-modal-scale">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-app-subtle">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-app-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 19a8 8 0 110-16 8 8 0 010 16z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t("commands.placeholder", "O que você quer fazer?")}
            className="flex-1 bg-transparent outline-none text-app-1 text-sm placeholder:text-app-3"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono text-app-3 bg-app-soft rounded border border-app-subtle">
            Esc
          </kbd>
        </div>

        {/* Lista */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {flatList.length === 0 ? (
            <div className="px-4 py-10 text-center text-app-3 text-sm">
              {t("commands.empty", "Nenhum comando encontrado.")}
            </div>
          ) : (
            SECTION_ORDER.map(sec => {
              const items = groupedCommands[sec];
              if (items.length === 0) return null;
              return (
                <div key={sec} className="mb-1">
                  <div className="px-4 pt-2 pb-1 text-[10px] font-semibold text-app-3 uppercase tracking-wider font-serif italic">
                    {t(SECTION_TITLES[sec])}
                  </div>
                  {items.map((cmd) => {
                    const flatIdx = flatList.indexOf(cmd);
                    const isActive = flatIdx === active;
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => cmd.perform(ctx)}
                        onMouseEnter={() => setActive(flatIdx)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          isActive ? "bg-app-hover" : ""
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 shrink-0 ${isActive ? "text-app-1" : "text-app-3"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path strokeLinecap="round" strokeLinejoin="round" d={cmd.icon} />
                        </svg>
                        <span className={`flex-1 text-sm ${isActive ? "text-app-1 font-medium" : "text-app-2"}`}>
                          {t(cmd.labelKey)}
                        </span>
                        {cmd.shortcut && (
                          <kbd className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                            isActive ? "bg-app-surface border-app text-app-2" : "bg-app-soft border-app-subtle text-app-3"
                          }`}>
                            {cmd.shortcut.toUpperCase()}
                          </kbd>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer com hint */}
        <div className="border-t border-app-subtle px-4 py-2 flex items-center gap-3 text-[10px] text-app-3">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-app-soft rounded border border-app-subtle font-mono">↑</kbd>
            <kbd className="px-1 py-0.5 bg-app-soft rounded border border-app-subtle font-mono">↓</kbd>
            navegar
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-app-soft rounded border border-app-subtle font-mono">↵</kbd>
            executar
          </span>
          <span className="ml-auto font-serif italic">Miller Platform</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
